import { formatToPercent } from 'helpers/formatters/number';
import { BenchmarkTypes } from 'types/Benchmark';
import {
  PerformanceReturnDetails,
  ReturnComponent,
} from 'types/PerformanceReturns';

export function getData(
  data?: PerformanceReturnDetails
): Array<ReturnComponent> {
  switch (data?.benchmark?.typeId) {
    case BenchmarkTypes.AbsoluteReturn:
    case BenchmarkTypes.CompositeReturn:
    case BenchmarkTypes.PullForward:
      return data?.returnComponents ?? [];
    case BenchmarkTypes.AssetWeighted:
    case BenchmarkTypes.CompositeWeighted:
    case BenchmarkTypes.StaticWeighted:
      return getWeightedData(data);
    default:
      return [];
  }
}

export function getColumns(typeId?: number) {
  switch (typeId) {
    case BenchmarkTypes.AbsoluteReturn:
    case BenchmarkTypes.CompositeReturn:
    case BenchmarkTypes.PullForward:
      return getCommonCols();
    case BenchmarkTypes.AssetWeighted:
      return getAssetWeightedCols();
    case BenchmarkTypes.CompositeWeighted:
      return getCompositeWeightedCols();
    case BenchmarkTypes.StaticWeighted:
      return getStaticWeightedCols();
    default:
      return [];
  }
}

function getWeightedData(data?: PerformanceReturnDetails) {
  const rows = [...(data?.returnComponents ?? [])];
  const totalRow = rows.reduce((sum, curVal) => {
    return {
      weight: (sum.weight ?? 0) + (curVal.weight ?? 0),
      return: (sum.return ?? 0) + (curVal.return ?? 0),
      contributionToReturn:
        (sum.contributionToReturn ?? 0) + (curVal.contributionToReturn ?? 0),
    };
  });
  totalRow.returnComponent = { name: 'Total' };
  rows.push(totalRow as ReturnComponent);
  return rows;
}

// absolute return, composite return,
// and pull forward have the same columns
function getCommonCols() {
  return [
    {
      Header: 'Benchmark',
      accessor: 'returnComponent.name',
      testid: 'benchmark-name',
    },
    {
      Header: 'Return',
      accessor: 'return',
      testid: 'return',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
  ];
}

function getAssetWeightedCols() {
  return [
    {
      Header: 'Fund',
      accessor: 'weightingComponent.name',
      testid: 'composite-name',
    },
    {
      Header: 'Benchmark',
      accessor: 'returnComponent.name',
      testid: 'benchmark-name',
    },
    {
      Header: 'Weight',
      accessor: 'weight',
      testid: 'weight',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Return',
      accessor: 'return',
      testid: 'return',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Cont. Return',
      accessor: 'contributionToReturn',
      testid: 'contributionToReturn',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
  ];
}

function getCompositeWeightedCols() {
  return [
    {
      Header: 'Composite',
      accessor: 'weightingComponent.name',
      testid: 'composite-name',
    },
    {
      Header: 'Benchmark',
      accessor: 'returnComponent.name',
      testid: 'benchmark-name',
    },
    {
      Header: 'Weight',
      accessor: 'weight',
      testid: 'weight',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Return',
      accessor: 'return',
      testid: 'return',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Cont. Return',
      accessor: 'contributionToReturn',
      testid: 'contributionToReturn',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
  ];
}

function getStaticWeightedCols() {
  return [
    {
      Header: 'Benchmark',
      accessor: 'returnComponent.name',
      testid: 'benchmark-name',
    },
    {
      Header: 'Weight',
      accessor: 'weight',
      testid: 'weight',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Return',
      accessor: 'return',
      testid: 'return',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Cont. Return',
      accessor: 'contributionToReturn',
      testid: 'contributionToReturn',
      className: 'text-right',
      Cell: (row: any) => (
        <div className='text-right'>{formatToPercent(row.value)}</div>
      ),
    },
    {
      Header: 'Attribute Assigned',
      testid: 'attribute-assigned',
      className: 'text-right',
      Cell: (row: any) => {
        return (
          <div className='text-right'>
            {row.row.original?.hadBasisPointAdjustmust
              ? 'BPS Adjustment'
              : row.row.original?.isLagged
              ? 'Lagged'
              : ''}
          </div>
        );
      },
    },
  ];
}
